import React, { useState, useEffect } from 'react';
import apiAdmin from '../services/api_admin';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import { parse, format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AnimatePresence, motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import AdminFilterBar, { AdminSearchField } from '../components/ui/AdminFilterBar';
import AdminPagination from '../components/ui/AdminPagination';
import { AdminLoadingState, AdminEmptyState } from '../components/ui/AdminStates';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import './CensoPresencial.css';

// ===== Helpers para a LOGO no PDF =====
const LOGO_PUBLIC_PATH = '/' + encodeURIComponent('censo g2_page-0001.jpg'); // "/censo%20g2_page-0001.jpg"

async function toDataURL(url: string): Promise<string> {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Interfaces
interface User {
  id_user: number;
  cpf: string;
  special_services: string;
  email: string;
}

interface Protocol {
  protocol: string;
  date: string;
  time: string;
  situation: string;
  id_protocol: number;
  id_user: number;
}

interface PersonalData {
  id_personal_data: number;
  id_user: number;
  name: string;
  sex: string;
  race: string;
  social_name: string;
  marital_status: string;
  date_of_birth: string;
  residential_phone: string;
  cell_phone: string;
  workplace_phone: string;
  current_capacity: string;
}

interface Row {
  id_user: number;
  cpf: string;
  email: string;
  name: string;
  token: string;
  protocol: string;
  situation: string;
  date: string;
}

interface Address {
  resides_outside_the_country: number | string;
  cep: string;
  type_of_street: string;
  street_address: string;
  complement: string;
  neighborhood: string;
  number: string;
  uf: string;
  city_of_residence: string;
  id_address: number;
  id_user: number;
}

interface Dependent {
  type_of_dependents: string;
  dependents_condition: string;
  name: string;
  date_of_birth: string;
  sex: string;
  nationality: string;
  id_dependents: number;
  id_user: number;
}

interface Declarant {
  type_of_person: string;
  name: string;
  cpf: string;
  date_of_birth: string;
  sex: string;
  start_date_of_representation: string;
  end_date_of_representation: string;
  telephone: string;
  identity_document: string;
  id_declarant: number;
  id_user: number;
}

interface Document {
  name: string;
  url: string;
  upload_date: string;
  id_docs: number;
  id_user: number;
  imageUrl?: string;
  imageStatus?: 'loading' | 'loaded' | 'error';
}

const PAGE_SIZE = 10;

const SITUATION_BADGE: Record<string, 'success' | 'warning' | 'neutral'> = {
  'Aprovado': 'success',
  'Documento Pendente': 'warning',
  'pendente': 'neutral',
};

type SectionKey = 'personalData' | 'address' | 'dependents' | 'declarants' | 'protocols' | 'documents';

const SECTION_LABELS: Record<SectionKey, string> = {
  personalData: 'Dados Pessoais',
  address: 'Endereço',
  dependents: 'Dependentes',
  declarants: 'Declarante',
  protocols: 'Protocolo',
  documents: 'Documentos',
};

const CensoPresencial: React.FC = () => {
  const { adminToken, logoutAdmin } = useAdminAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState<Row[]>([]);
  const [filteredRows, setFilteredRows] = useState<Row[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Semana (type="week")
  const [weekFilter, setWeekFilter] = useState('');

  // Datas personalizadas (YYYY-MM-DD)
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<Row | null>(null);
  const [personalData, setPersonalData] = useState<PersonalData | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [declarants, setDeclarants] = useState<Declarant[]>([]);
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [protocolSituations, setProtocolSituations] = useState<{ [key: number]: string }>({});
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    personalData: false,
    address: false,
    dependents: false,
    declarants: false,
    protocols: false,
    documents: false,
  });

  // Utilitários
  const loadImage = async (filename: string): Promise<string> => {
    try {
      const response = await apiAdmin.get(`/images/${filename}`, {
        responseType: 'blob',
        timeout: 60000, // 60s para download de imagens
      });
      return URL.createObjectURL(response.data);
    } catch (err: any) {
      console.error(`Erro ao carregar imagem "${filename}":`, err?.response?.status, err?.message);
      return '';
    }
  };

  const formatBrazilianDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) return format(date, 'dd/MM/yyyy');

      const tryParse = (s: string, f: string) => {
        const d = parse(s, f, new Date());
        return isNaN(d.getTime()) ? null : d;
      };

      const candidates = [
        tryParse(dateString, 'dd/MM/yyyy'),
        tryParse(dateString, 'yyyy/MM/dd'),
        tryParse(dateString, 'MM-dd-yyyy'),
        tryParse(dateString, 'yyyy.MM.dd'),
        tryParse(dateString, 'dd-MM-yyyy'),
      ].filter(Boolean) as Date[];

      return candidates.length ? format(candidates[0], 'dd/MM/yyyy') : dateString;
    } catch {
      return dateString;
    }
  };

  const normalizeDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) return format(date, 'yyyy-MM-dd');

      const tryParse = (s: string, f: string) => {
        const d = parse(s, f, new Date());
        return isNaN(d.getTime()) ? null : d;
      };

      const candidates = [
        tryParse(dateString, 'dd/MM/yyyy'),
        tryParse(dateString, 'yyyy/MM/dd'),
        tryParse(dateString, 'MM-dd-yyyy'),
        tryParse(dateString, 'yyyy.MM.dd'),
        tryParse(dateString, 'dd-MM-yyyy'),
      ].filter(Boolean) as Date[];

      return candidates.length ? format(candidates[0], 'yyyy-MM-dd') : '';
    } catch {
      return '';
    }
  };

  const getWeekRange = (weekString: string): { startDate: string; endDate: string } => {
    if (!weekString) return { startDate: '', endDate: '' };
    const [year, week] = weekString.split('-W').map(Number);
    const janFirst = new Date(year, 0, 1);
    const firstSunday = new Date(janFirst);
    firstSunday.setDate(janFirst.getDate() - janFirst.getDay());
    const startDate = new Date(firstSunday);
    startDate.setDate(firstSunday.getDate() + (week - 1) * 7);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      startDate: `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`,
      endDate: `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`,
    };
  };

  const formatWeekRange = (weekString: string): string => {
    const { startDate, endDate } = getWeekRange(weekString);
    if (!startDate || !endDate) return 'Nenhuma semana selecionada';
    return `${formatBrazilianDate(startDate)} a ${formatBrazilianDate(endDate)}`;
  };

  // ===== PDF com LOGO no topo =====
  const handleGeneratePDF = async () => {
    if (filteredRows.length === 0) return;

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const leftMargin = 20;

      let cursorY = 10; // margem superior inicial

      // ===== 1) LOGO MENOR (respeita largura/altura máx. e mantém proporção) =====
      try {
        const logoDataUrl = await toDataURL(LOGO_PUBLIC_PATH);
        const imgProps = doc.getImageProperties(logoDataUrl);

        // Ajuste aqui os limites desejados da logo no PDF
        const MAX_W = 80; // largura máxima da logo em mm (antes era até 160)
        const MAX_H = 25; // altura máxima da logo em mm

        const ratio = imgProps.width / imgProps.height;
        let logoW = Math.min(MAX_W, pageWidth - leftMargin * 2);
        let logoH = logoW / ratio;

        if (logoH > MAX_H) {
          logoH = MAX_H;
          logoW = logoH * ratio;
        }

        const x = (pageWidth - logoW) / 2;

        // Detecta tipo de imagem para o addImage
        const isPng = LOGO_PUBLIC_PATH.toLowerCase().endsWith('.png') || logoDataUrl.startsWith('data:image/png');
        doc.addImage(logoDataUrl, isPng ? 'PNG' : 'JPEG', x, cursorY, logoW, logoH);

        cursorY += logoH + 6; // espaço depois da logo
      } catch (e) {
        // se a logo não carregar, segue sem ela
        cursorY = 18;
        console.warn('Logo não encontrada em /public. Prosseguindo sem imagem.');
      }

      // ===== 2) Cabeçalho textual =====
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Censo Prev - Relatório', leftMargin, cursorY);
      cursorY += 8;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');

      const parts: string[] = [];
      if (weekFilter) parts.push(`Semana: ${formatWeekRange(weekFilter)}`);
      if (startDateFilter || endDateFilter) {
        const inicio = startDateFilter ? formatBrazilianDate(startDateFilter) : '—';
        const fim = endDateFilter ? formatBrazilianDate(endDateFilter) : '—';
        parts.push(`Período: ${inicio} a ${fim}`);
      }
      const periodo = parts.length ? parts.join(' | ') : 'Sem filtros de data';
      doc.text(periodo, leftMargin, cursorY);
      cursorY += 6;

      // ===== 3) Tabela =====
      autoTable(doc, {
        startY: cursorY,
        head: [['Nome', 'CPF', 'Email', 'Token', 'Protocolo', 'Situação', 'Data']],
        body: filteredRows.map((row) => [
          row.name,
          row.cpf,
          row.email,
          row.token,
          row.protocol,
          row.situation,
          formatBrazilianDate(row.date) || row.date,
        ]),
        styles: { font: 'helvetica', fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [25, 85, 191], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        tableWidth: 'auto',
        // importante: margem lateral; o topo é controlado por startY
        margin: { left: leftMargin, right: 20 },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: 24 },
          2: { cellWidth: 36 },
          3: { cellWidth: 24 },
          4: { cellWidth: 28 },
          5: { cellWidth: 26 },
          6: { cellWidth: 20 },
        },
      });

      const fileSuffix = [
        weekFilter ? weekFilter : 'todas_semanas',
        startDateFilter || '—',
        endDateFilter || '—',
      ].join('_');

      doc.save(`relatorio_censo_${fileSuffix}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    }
  };

  // Carregamento principal
  useEffect(() => {
    if (!adminToken) {
      logoutAdmin();
      navigate('/rj/admin-login');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const usersRes = await apiAdmin.get<{ users: User[] }>(`/auth/users`);
        const userMap = new Map<number, User & { token: string }>();
        usersRes.data.users.forEach((u) => {
          const token =
            Math.random().toString(36).substring(2, 10).toUpperCase() +
            Date.now().toString(36).substring(0, 4).toUpperCase();
          userMap.set(u.id_user, { ...u, token });
        });

        const personalDataRes = await apiAdmin.get<{ data: PersonalData[] }>(`/personaldata`);
        const personalDataMap = new Map<number, PersonalData>();
        personalDataRes.data.data.forEach((pd) => personalDataMap.set(pd.id_user, pd));

        const protocolsRes = await apiAdmin.get<{ data: Protocol[] }>(`/protocol`);
        const protocolsByUser = protocolsRes.data.data.reduce((acc, protocol) => {
          if (!acc[protocol.id_user]) acc[protocol.id_user] = [];
          acc[protocol.id_user].push(protocol);
          return acc;
        }, {} as { [key: number]: Protocol[] });

        const newRows: Row[] = [];
        for (const id_user in protocolsByUser) {
          const user = userMap.get(Number(id_user));
          const pData = personalDataMap.get(Number(id_user));

          // Um protocolo nunca deve sumir da lista só porque /auth/users não
          // retornou aquele usuário (paginação, filtro do backend, etc.) —
          // isso já causou protocolos reais ficarem invisíveis no painel.
          // Em vez de descartar a linha, mostramos com os campos indisponíveis
          // marcados, e avisamos no console para investigação.
          if (!user) {
            console.warn(
              `Censo Online: protocolo(s) do id_user=${id_user} não têm usuário correspondente em /auth/users — exibindo com dados parciais.`
            );
          }

          const sortedProtocols = protocolsByUser[id_user].sort((a, b) => {
            const dateA = normalizeDate(a.date) || '0000-00-00';
            const dateB = normalizeDate(b.date) || '0000-00-00';
            return dateB.localeCompare(dateA);
          });

          const latestProtocol = sortedProtocols[0];
          if (latestProtocol) {
            newRows.push({
              id_user: latestProtocol.id_user,
              cpf: user?.cpf || 'Não encontrado',
              email: user?.email || 'Não encontrado',
              name: pData?.name || 'Nome não disponível',
              token: user?.token || '—',
              protocol: latestProtocol.protocol || '',
              situation: latestProtocol.situation || '',
              date: latestProtocol.date || '',
            });
          }
        }

        setRows(newRows);
        setFilteredRows(newRows);
      } catch (error: any) {
        console.error('Erro ao carregar dados:', error?.response || error);
        if (error?.response?.status === 401) {
          logoutAdmin();
          navigate('/rj/admin-login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adminToken, logoutAdmin, navigate]);

  // FILTROS (busca texto + semana + intervalo personalizado)
  useEffect(() => {
    let filtered = [...rows];

    // texto
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((row) =>
        (row.cpf?.toLowerCase().includes(q) || false) ||
        (row.email?.toLowerCase().includes(q) || false) ||
        (row.name?.toLowerCase().includes(q) || false) ||
        (row.token?.toLowerCase().includes(q) || false) ||
        (row.protocol?.toLowerCase().includes(q) || false) ||
        (row.situation?.toLowerCase().includes(q) || false)
      );
    }

    // semana
    let weekStart = '';
    let weekEnd = '';
    if (weekFilter) {
      const r = getWeekRange(weekFilter);
      weekStart = r.startDate;
      weekEnd = r.endDate;
      filtered = filtered.filter((row) => {
        const d = normalizeDate(row.date);
        return d && d >= weekStart && d <= weekEnd;
      });
    }

    // intervalo personalizado
    const start = startDateFilter || '';
    const end = endDateFilter || '';
    if (start || end) {
      filtered = filtered.filter((row) => {
        const d = normalizeDate(row.date);
        if (!d) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      });
    }

    setFilteredRows(filtered);
    setPage(1);
  }, [searchTerm, weekFilter, startDateFilter, endDateFilter, rows]);

  // Detalhes
  const fetchDetails = async (id_user: number) => {
    if (!adminToken) {
      logoutAdmin();
      navigate('/rj/admin-login');
      return;
    }

    setLoadingDetails(true);
    try {
      const personalRes = await apiAdmin.get<{ message: string; data: PersonalData }>(`/personaldata/${id_user}`);
      setPersonalData(personalRes.data.data);

      const addressRes = await apiAdmin.get<{ message: string; data: Address }>(`/address/${id_user}`);
      setAddress(addressRes.data.data);

      const dependentsRes = await apiAdmin.get<{ message: string; count: number; data: Dependent[] }>(`/dependents/${id_user}`);
      setDependents(dependentsRes.data.data);

      const declarantsRes = await apiAdmin.get<{ message: string; data: Declarant }>(`/declarant/${id_user}`);
      const decl = declarantsRes.data.data;
      const sortedDeclarants = decl ? [decl].filter((d) => d.name || d.type_of_person) : [];
      const latestDeclarant = sortedDeclarants.sort((a, b) => {
        const dateA = normalizeDate(a.start_date_of_representation) || '0000-00-00';
        const dateB = normalizeDate(b.start_date_of_representation) || '0000-00-00';
        return dateB.localeCompare(dateA);
      });
      setDeclarants(latestDeclarant.length > 0 ? [latestDeclarant[0]] : []);

      const protocolsRes = await apiAdmin.get<{ message: string; count: number; data: Protocol[] }>(`/protocol/user/${id_user}`);
      const sortedProtocols = protocolsRes.data.data.sort((a, b) => {
        const dateA = normalizeDate(a.date) || '0000-00-00';
        const dateB = normalizeDate(b.date) || '0000-00-00';
        return dateB.localeCompare(dateA);
      });
      const latestProtocol = sortedProtocols.length > 0 ? [sortedProtocols[0]] : [];
      setProtocols(latestProtocol);

      const initialSituations = latestProtocol.reduce((acc, p) => ({ ...acc, [p.id_protocol]: p.situation }), {});
      setProtocolSituations(initialSituations);
    } catch (error: any) {
      console.error('Erro ao carregar detalhes do usuário:', error?.response || error);
    } finally {
      setLoadingDetails(false);
    }

    // Os documentos têm carregamento e erro isolados dos demais campos:
    // uma falha (ou um simples "usuário sem documentos") em /docs/user/:id
    // não pode fazer a seção sumir sem explicação nem travar o resto do modal.
    fetchDocuments(id_user);
  };

  const fetchDocuments = async (id_user: number) => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const documentsRes = await apiAdmin.get<{ message: string; count: number; data: Document[] }>(`/docs/user/${id_user}`);
      const initialDocuments: Document[] = (documentsRes.data.data || []).map((doc) => ({ ...doc, imageStatus: 'loading' }));
      setDocuments(initialDocuments);

      // As miniaturas carregam em segundo plano, uma a uma, para não travar
      // a seção inteira esperando todas as imagens (uma foto com defeito
      // não deve impedir as demais de aparecer).
      initialDocuments.forEach((doc) => {
        loadDocumentImage(doc);
      });
    } catch (error: any) {
      // Alguns back-ends respondem 404 quando o usuário simplesmente não tem
      // nenhum documento enviado — isso é uma lista vazia, não uma falha.
      if (error?.response?.status === 404) {
        setDocuments([]);
      } else {
        console.error('Erro ao carregar documentos do usuário:', error?.response || error);
        setDocuments([]);
        setDocumentsError(error?.response?.data?.message || 'Falha ao carregar os documentos enviados por este usuário.');
      }
    } finally {
      setDocumentsLoading(false);
    }
  };

  const loadDocumentImage = async (doc: Document) => {
    const filename = doc.url.split('/').pop();
    const imageUrl = filename ? await loadImage(filename) : '';
    setDocuments((prev) =>
      prev.map((d) => (d.id_docs === doc.id_docs ? { ...d, imageUrl, imageStatus: imageUrl ? 'loaded' : 'error' } : d))
    );
  };

  const clearWeekFilter = () => setWeekFilter('');
  const clearCustomDates = () => { setStartDateFilter(''); setEndDateFilter(''); };

  const handleRowClick = (row: Row) => {
    setSelectedRow(row);
    fetchDetails(row.id_user);
    // "Documentos" já abre expandida para que as fotos enviadas pelo usuário
    // apareçam de imediato, sem precisar de um clique extra no acordeão.
    setExpandedSections({
      personalData: false, address: false, dependents: false, declarants: false, protocols: false, documents: true,
    });
  };

  const closeDetails = () => {
    setSelectedRow(null);
    setPersonalData(null);
    setAddress(null);
    setDependents([]);
    setDeclarants([]);
    setProtocols([]);
    setDocuments([]);
    setDocumentsError(null);
    setSelectedImage(null);
    setProtocolSituations({});
    setExpandedSections({
      personalData: false, address: false, dependents: false, declarants: false, protocols: false, documents: false,
    });
  };

  const handleSituationChange = (id_protocol: number, newSituation: string) => {
    setProtocolSituations((prev) => ({ ...prev, [id_protocol]: newSituation }));
  };

  const updateProtocolSituation = async (id_user: number, id_protocol: number) => {
    if (!adminToken) {
      logoutAdmin();
      navigate('/rj/admin-login');
      return;
    }
    try {
      const protocol = protocols.find((p) => p.id_protocol === id_protocol);
      if (!protocol) return;

      const updatedProtocol = {
        protocol: protocol.protocol,
        date: format(new Date(protocol.date), 'yyyy-MM-dd'),
        time: protocol.time,
        situation: protocolSituations[id_protocol] || protocol.situation,
      };

      await apiAdmin.put(`/protocol/${id_protocol}`, updatedProtocol);

      setRows((prev) =>
        prev.map((row) =>
          row.id_user === id_user && row.protocol === protocol.protocol
            ? { ...row, situation: protocolSituations[id_protocol] }
            : row
        )
      );
      setFilteredRows((prev) =>
        prev.map((row) =>
          row.id_user === id_user && row.protocol === protocol.protocol
            ? { ...row, situation: protocolSituations[id_protocol] }
            : row
        )
      );
      setProtocols((prev) =>
        prev.map((p) =>
          p.id_protocol === id_protocol ? { ...p, situation: protocolSituations[id_protocol] } : p
        )
      );
    } catch (error: any) {
      console.error('Erro ao atualizar situação:', error?.response || error);
    }
  };

  const toggleSection = (section: SectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE) || 1;
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const chips = [
    searchTerm && { key: 'search', label: `Busca: ${searchTerm}`, onRemove: () => setSearchTerm('') },
    weekFilter && { key: 'week', label: `Semana: ${formatWeekRange(weekFilter)}`, onRemove: clearWeekFilter },
    (startDateFilter || endDateFilter) && {
      key: 'range',
      label: `Período: ${formatBrazilianDate(startDateFilter) || '—'} a ${formatBrazilianDate(endDateFilter) || '—'}`,
      onRemove: clearCustomDates,
    },
  ].filter(Boolean) as { key: string; label: string; onRemove: () => void }[];

  const renderAccordionSection = (key: SectionKey, visible: boolean, content: React.ReactNode) => {
    if (!visible) return null;
    const isOpen = expandedSections[key];
    return (
      <div className="accordion-section">
        <button type="button" className="accordion-header" onClick={() => toggleSection(key)} aria-expanded={isOpen}>
          {SECTION_LABELS[key]}
          <svg
            className={`accordion-chevron ${isOpen ? 'accordion-chevron--open' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <div className="accordion-content">{content}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <AdminLayout title="Censo Online">
      <section className="admin-card">
        <div className="admin-card__head">
          <h2>Usuários, Tokens e Protocolos</h2>
          <Button variant="ghost" onClick={handleGeneratePDF} disabled={filteredRows.length === 0}>
            Gerar PDF
          </Button>
        </div>

        <AdminFilterBar chips={chips} resultCount={filteredRows.length} resultLabel="registros">
          <AdminSearchField
            id="censo-search"
            label="Busca geral"
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Nome, CPF, email, token, protocolo ou situação"
          />
          <div className="admin-filter-field">
            <label htmlFor="week-filter">Semana</label>
            <input
              id="week-filter"
              type="week"
              value={weekFilter}
              onChange={(e) => setWeekFilter(e.target.value)}
              className="admin-input"
            />
          </div>
          <div className="admin-filter-field">
            <label htmlFor="start-date-filter">Início</label>
            <input
              id="start-date-filter"
              type="date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              className="admin-input"
            />
          </div>
          <div className="admin-filter-field">
            <label htmlFor="end-date-filter">Fim</label>
            <input
              id="end-date-filter"
              type="date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              className="admin-input"
            />
          </div>
        </AdminFilterBar>

        {loading ? (
          <AdminLoadingState label="Carregando dados..." />
        ) : filteredRows.length === 0 ? (
          <AdminEmptyState message="Nenhum resultado encontrado para os filtros aplicados." />
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>CPF</th>
                    <th>Email</th>
                    <th>Token</th>
                    <th>Protocolo</th>
                    <th>Situação</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((r, index) => (
                    <tr key={index} onClick={() => handleRowClick(r)} className="clickable-row">
                      <td>{r.name}</td>
                      <td>{r.cpf}</td>
                      <td>{r.email}</td>
                      <td>{r.token}</td>
                      <td>{r.protocol}</td>
                      <td>
                        <span className={`badge badge--${SITUATION_BADGE[r.situation] || 'neutral'}`}>{r.situation}</span>
                      </td>
                      <td>{formatBrazilianDate(r.date) || r.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </section>

      <AnimatePresence>
        {selectedRow && (
          <motion.div
            className="details-modal-overlay"
            onClick={closeDetails}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <motion.div
              className="details-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <button className="close-button" onClick={closeDetails} aria-label="Fechar detalhes">×</button>
              <h2>Detalhes do Usuário</h2>
              {loadingDetails ? (
                <AdminLoadingState label="Carregando detalhes..." />
              ) : (
                <div className="accordion-container">
                  {renderAccordionSection(
                    'personalData',
                    !!personalData,
                    personalData && (
                      <>
                        <div className="form-group"><label>Nome</label><input type="text" value={personalData.name} readOnly /></div>
                        <div className="form-group"><label>Sexo</label><input type="text" value={personalData.sex} readOnly /></div>
                        <div className="form-group"><label>Raça</label><input type="text" value={personalData.race} readOnly /></div>
                        <div className="form-group"><label>Nome Social</label><input type="text" value={personalData.social_name} readOnly /></div>
                        <div className="form-group"><label>Estado Civil</label><input type="text" value={personalData.marital_status} readOnly /></div>
                        <div className="form-group"><label>Data de Nascimento</label><input type="text" value={formatBrazilianDate(personalData.date_of_birth)} readOnly /></div>
                        <div className="form-group"><label>Telefone Residencial</label><input type="text" value={personalData.residential_phone} readOnly /></div>
                        <div className="form-group"><label>Celular</label><input type="text" value={personalData.cell_phone} readOnly /></div>
                        <div className="form-group"><label>Telefone do Trabalho</label><input type="text" value={personalData.workplace_phone} readOnly /></div>
                        <div className="form-group"><label>Capacidade Atual</label><input type="text" value={personalData.current_capacity} readOnly /></div>
                      </>
                    )
                  )}

                  {renderAccordionSection(
                    'address',
                    !!address,
                    address && (
                      <>
                        <div className="form-group"><label>Reside Fora do País</label><input type="text" value={address.resides_outside_the_country} readOnly /></div>
                        <div className="form-group"><label>CEP</label><input type="text" value={address.cep} readOnly /></div>
                        <div className="form-group"><label>Tipo de Rua</label><input type="text" value={address.type_of_street} readOnly /></div>
                        <div className="form-group"><label>Endereço</label><input type="text" value={address.street_address} readOnly /></div>
                        <div className="form-group"><label>Complemento</label><input type="text" value={address.complement} readOnly /></div>
                        <div className="form-group"><label>Bairro</label><input type="text" value={address.neighborhood} readOnly /></div>
                        <div className="form-group"><label>Número</label><input type="text" value={address.number} readOnly /></div>
                        <div className="form-group"><label>UF</label><input type="text" value={address.uf} readOnly /></div>
                        <div className="form-group"><label>Cidade de Residência</label><input type="text" value={address.city_of_residence} readOnly /></div>
                      </>
                    )
                  )}

                  {renderAccordionSection(
                    'dependents',
                    dependents.length > 0,
                    <div className="admin-table-wrap">
                      <table className="dependents-table">
                        <thead>
                          <tr>
                            <th>Tipo de Dependente</th>
                            <th>Condição</th>
                            <th>Nome</th>
                            <th>Data de Nascimento</th>
                            <th>Sexo</th>
                            <th>Nacionalidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dependents.map((d) => (
                            <tr key={d.id_dependents}>
                              <td>{d.type_of_dependents}</td>
                              <td>{d.dependents_condition}</td>
                              <td>{d.name}</td>
                              <td>{formatBrazilianDate(d.date_of_birth)}</td>
                              <td>{d.sex}</td>
                              <td>{d.nationality}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {renderAccordionSection(
                    'declarants',
                    declarants.length > 0,
                    <div className="admin-table-wrap">
                      <table className="dependents-table">
                        <thead>
                          <tr>
                            <th>Tipo de Pessoa</th>
                            <th>Nome</th>
                            <th>CPF</th>
                            <th>Data de Nascimento</th>
                            <th>Sexo</th>
                            <th>Início da Representação</th>
                            <th>Fim da Representação</th>
                            <th>Telefone</th>
                            <th>Documento de Identidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {declarants.map((d) => (
                            <tr key={d.id_declarant}>
                              <td>{d.type_of_person}</td>
                              <td>{d.name}</td>
                              <td>{d.cpf}</td>
                              <td>{formatBrazilianDate(d.date_of_birth)}</td>
                              <td>{d.sex}</td>
                              <td>{formatBrazilianDate(d.start_date_of_representation)}</td>
                              <td>{formatBrazilianDate(d.end_date_of_representation)}</td>
                              <td>{d.telephone}</td>
                              <td>{d.identity_document}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {renderAccordionSection(
                    'protocols',
                    protocols.length > 0,
                    <>
                      <div className="form-group">
                        <label>Token</label>
                        <input type="text" value={selectedRow?.token || ''} readOnly />
                      </div>
                      <div className="admin-table-wrap">
                        <table className="dependents-table">
                          <thead>
                            <tr>
                              <th>Protocolo</th>
                              <th>Data</th>
                              <th>Hora</th>
                              <th>Situação</th>
                              <th>Ação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {protocols.map((p) => (
                              <tr key={p.id_protocol}>
                                <td>{p.protocol}</td>
                                <td>{formatBrazilianDate(p.date)}</td>
                                <td>{p.time}</td>
                                <td>
                                  <select
                                    className="admin-select"
                                    value={protocolSituations[p.id_protocol] || p.situation}
                                    onChange={(e) => handleSituationChange(p.id_protocol, e.target.value)}
                                  >
                                    <option value="Aprovado">Aprovado</option>
                                    <option value="Documento Pendente">Documento Pendente</option>
                                    <option value="Em análise">Em análise</option>
                                    <option value="pendente">Pendente</option>
                                  </select>
                                </td>
                                <td>
                                  <Button onClick={() => updateProtocolSituation(p.id_user, p.id_protocol)}>
                                    Atualizar
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {renderAccordionSection(
                    'documents',
                    true,
                    documentsLoading && documents.length === 0 ? (
                      <AdminLoadingState label="Carregando documentos..." />
                    ) : documentsError ? (
                      <div className="documents-error">
                        <p>{documentsError}</p>
                        <Button
                          variant="ghost"
                          type="button"
                          onClick={() => selectedRow && fetchDocuments(selectedRow.id_user)}
                        >
                          Tentar novamente
                        </Button>
                      </div>
                    ) : documents.length === 0 ? (
                      <AdminEmptyState message="Este usuário ainda não enviou nenhum documento ou foto." />
                    ) : (
                      <div className="documents-grid">
                        {documents.map((d) => (
                          <div key={d.id_docs} className="document-item">
                            {d.imageStatus === 'loaded' && d.imageUrl ? (
                              <img
                                src={d.imageUrl}
                                alt={d.name}
                                onClick={() => setSelectedImage(d.imageUrl ?? null)}
                                className="clickable-image"
                              />
                            ) : d.imageStatus === 'error' ? (
                              <div className="document-item__placeholder document-item__placeholder--error">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" />
                                  <line x1="9" y1="9" x2="15" y2="15" />
                                  <line x1="15" y1="9" x2="9" y2="15" />
                                </svg>
                                <span>Não foi possível carregar</span>
                                <button type="button" onClick={() => loadDocumentImage(d)}>
                                  Tentar novamente
                                </button>
                              </div>
                            ) : (
                              <div className="document-item__placeholder">
                                <Spinner />
                                <span>Carregando imagem...</span>
                              </div>
                            )}
                            <p className="document-item__name">{d.name}</p>
                            <p className="document-item__date">Upload: {formatBrazilianDate(d.upload_date)}</p>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="image-modal-overlay"
            onClick={() => setSelectedImage(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <motion.div
              className="image-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <button className="close-button" onClick={() => setSelectedImage(null)} aria-label="Fechar imagem">×</button>
              <img src={selectedImage} alt="Visualização ampliada" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default CensoPresencial;
